import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useGetTokenPriceQuery } from '../../redux/api/public/tokens';
import {
  BodyMedium,
  ParagraphMediumText,
  Heading4Text,
  BodySmall,
} from '../Texts';
import { formatTokenPrice } from '../../common/currency';
import { selectTokenBalance } from '../../redux/slices/walletSlice';
import { selectTokenByNetworkAndSymbol } from '../../redux/slices/tokenSlice';
import { useDispatch, useSelector } from 'react-redux';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePrivy } from '@privy-io/react-auth';
import { hapticsImpactLight } from '../../common/haptic';
import TokenActions from '../TokenActions/index';
import { openModal } from '../../redux/slices/modals';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import useCustomLogin from '../../hooks/useCustomLogin';

const TokenInformationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 0px;
  }
`;

const TokenPriceInfo = styled(ParagraphMediumText)`
  color: ${({ theme }) => theme.text.secondary};
`;

const DescriptionContainer = styled.div`
  position: relative;
`;

const Description = styled(BodyMedium)`
  ${({ $isExpanded }) =>
    !$isExpanded &&
    `
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
  `}
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.primary.primary};
  padding: 0;
  font-size: 14px;
  cursor: pointer;
  margin-top: 4px;
  font-family: 'Inter', sans-serif;
  display: none;
  display: block;
`;

const TokenSymbolText = styled.span`
  font-size: 19px;
  color: ${({ theme }) => theme.text.secondary};
  line-height: 30px;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 16px;
  }
`;

const TokenNameContainer = styled.p``;

const TokenNameStyle = styled.span`
  font-size: 34px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 22px;
  }
`;

const ContentRow = styled.div`
  display: flex;
  gap: 30px;
  color: ${({ theme }) => theme.text.regular};
  justify-content: flex-start;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

const TokenLogo = styled.img`
  width: 149px;
  height: 149px;
  @media ${PHONE_MEDIA_QUERY} {
    width: 60px;
    height: 60px;
  }
`;

const TokenDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const TokenBalanceContainer = styled.div`
  display: flex;
`;

const TokenBalance = styled(Heading4Text)`
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 20px;
  }
`;

const TokenBalanceText = styled(Heading4Text)`
  color: ${({ theme }) => theme.text.secondary};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 20px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

const BackIcon = styled.img`
  cursor: pointer;
  width: 25px;
  height: 25px;
  @media ${PHONE_MEDIA_QUERY} {
    width: 14px;
    height: 20px;
  }
`;

const TokenInformation = ({ token }) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const navigate = useNavigate();
  const { user } = usePrivy();
  const theme = useTheme();
  const { login } = useCustomLogin();
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'USDC'),
  );

  const dispatch = useDispatch();

  const tokenBalance = useSelector((state) =>
    selectTokenBalance(token?.network, token?.address || '')(state),
  );

  const { data: tokenPrice } = useGetTokenPriceQuery(token?.address, {
    skip: !token?.address,
    pollingInterval: 10000,
  });
  const tokenPriceUSD = tokenPrice?.priceUSDC;
  const tokenBalanceUSD = tokenPriceUSD
    ? parseFloat(tokenPriceUSD) * tokenBalance
    : 0;

  useEffect(() => {
    const el = descriptionRef.current;
    if (el && !isExpanded) {
      setIsOverflowing(el.scrollHeight > el.clientHeight);
    }
  }, [token?.description, isExpanded]);

  const TokenActionsComponent = (
    <TokenActions
      onGiftClick={() => {
        navigate(
          `/app/gifts/create?tokenAddress=${token.address}&network=${token.network}`,
        );
      }}
      onTradeClick={() => {
        navigate(`/app/trade?tokenAddress=${token.address}`);
      }}
      onSendClick={() => {
        if (!user) {
          login();
          return;
        }
        dispatch(
          openModal({
            name: 'withdrawFunds',
            props: { initialSelectedToken: token.symbol },
          }),
        );
      }}
      onReceiveClick={() => {
        if (!user) {
          login();
          return;
        }
        dispatch(
          openModal({
            name: 'addFunds',
            props: { token: true },
          }),
        );
      }}
    />
  );

  return (
    <>
      <ContentRow>
        <LogoContainer>
          <ChevronLeftIcon
            width={isMobile ? 14 : 25}
            height={isMobile ? 20 : 25}
            color={theme.text.title}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              hapticsImpactLight();
              const back = searchParams.get('back');
              if (back) {
                navigate(back);
              } else {
                if (user) {
                  navigate('/app/portfolio');
                } else {
                  navigate('/app/home');
                }
              }
            }}
          />
          <TokenLogo src={token?.logoURI} />
        </LogoContainer>
        <TokenDescriptionContainer>
          <TokenInformationContainer>
            <TokenNameContainer>
              <TokenNameStyle>{token?.name}</TokenNameStyle>{' '}
              <TokenSymbolText>({token?.symbol})</TokenSymbolText>
            </TokenNameContainer>
            <TokenPriceInfo>
              1 {token?.symbol} = $
              {formatTokenPrice(parseFloat(tokenPrice?.priceUSDC))}
            </TokenPriceInfo>
            {!isMobile && (
              <>
                <div>
                  <BodySmall style={{ color: theme.text.title }}>
                    Your Balance
                  </BodySmall>
                  <TokenBalanceContainer>
                    <TokenBalance>${tokenBalanceUSD?.toFixed(2)}</TokenBalance>
                    <TokenBalanceText>
                      &nbsp;({formatTokenPrice(tokenBalance, tokenBalanceUSD)}{' '}
                      {token?.symbol})
                    </TokenBalanceText>
                  </TokenBalanceContainer>
                </div>
                <DescriptionContainer>
                  <Description ref={descriptionRef} $isExpanded={isExpanded}>
                    {token?.description}
                  </Description>
                  {token?.description && isOverflowing && (
                    <ReadMoreButton onClick={() => setIsExpanded(!isExpanded)}>
                      {isExpanded ? 'Show less' : 'Read more'}
                    </ReadMoreButton>
                  )}
                </DescriptionContainer>
                <BodyMedium>
                  <a
                    style={{ color: theme.primary.primary }}
                    href={token?.projectURI}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    View Project
                  </a>
                </BodyMedium>
              </>
            )}
            {isMobile && (
              <BodyMedium>
                <a
                  style={{ color: theme.primary.primary }}
                  href={token?.projectURI}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  View Project
                </a>
              </BodyMedium>
            )}
          </TokenInformationContainer>
        </TokenDescriptionContainer>
        {!isMobile && (
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {TokenActionsComponent}
          </div>
        )}
      </ContentRow>
      {isMobile && (
        <>
          <ContentRow>
            <div>
              <BodySmall style={{ color: theme.text.secondary }}>
                Your Balance
              </BodySmall>
              <TokenBalanceContainer>
                <TokenBalance>${tokenBalanceUSD?.toFixed(2)}</TokenBalance>
                <TokenBalanceText>
                  &nbsp;({formatTokenPrice(tokenBalance, tokenBalanceUSD)}{' '}
                  {token?.symbol})
                </TokenBalanceText>
              </TokenBalanceContainer>
            </div>
          </ContentRow>
        </>
      )}
      {isMobile && (
        <div
          style={{
            width: '100%',
          }}
        >
          {TokenActionsComponent}
        </div>
      )}
    </>
  );
};

export default TokenInformation;
